import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Check, FileText, Plus, Trash2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  useAcceptContentPlanFlag,
  useCommitContentPlanImport,
  useDiscardContentPlanImport,
  useGetContentPlanFileUrl,
  useGetContentPlanImport,
  useSaveContentPlanRow,
  useScheduleContentPlanImport,
  useSetContentPlanImport,
} from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import {
  PLAN_ACTION,
  commitReportLine,
  conflictCount,
  countOf,
  monthLabel,
  openFlag,
  planHeading,
  planMixLines,
  shortDate,
  type CodeLabel,
  type PlanCaption,
  type PlanImportRow,
  type PlanReview as Review,
} from '../../utils/marketing'

const WEEKDAYS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' },
]

/** A field the reader could not find word for word, said beside the field. */
function FlagNote({ flag, canAct, onAccept }: { flag: ReturnType<typeof openFlag>; canAct: boolean; onAccept: () => void }) {
  if (!flag) return null
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-rose-700 dark:text-rose-300">
      <span>Not found word for word in the document. Fix it to match, or accept it if the change is deliberate.</span>
      {canAct && (
        <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onAccept}>
          Accept as written
        </Button>
      )}
    </div>
  )
}

function RowCard({
  row,
  audiences,
  canEdit,
  onSave,
  onAccept,
}: {
  row: PlanImportRow
  audiences: CodeLabel[]
  canEdit: boolean
  onSave: (patch: Record<string, unknown>) => Promise<void>
  onAccept: (field: string) => Promise<void>
}) {
  const [captions, setCaptions] = useState<PlanCaption[]>(row.captions)
  useEffect(() => setCaptions(row.captions), [row.captions])
  const action = PLAN_ACTION[row.action]
  const blur = (field: keyof PlanImportRow) => (e: { target: { value: string } }) => {
    const before = (row[field] ?? '') as string
    if (before === e.target.value) return
    void onSave({ [field]: e.target.value })
  }
  const saveCaptions = (next: PlanCaption[]) => {
    setCaptions(next)
    void onSave({ captions: next })
  }
  const red = (field: string) => (openFlag(row, field) ? 'border-rose-400 bg-rose-50/60 dark:bg-rose-950/30' : '')

  return (
    <li className={cn('space-y-3 px-4 py-4', !row.include && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={row.include}
            disabled={!canEdit}
            onChange={(e) => void onSave({ include: e.target.checked })}
          />
          Post {row.post_no}
        </label>
        <Chip tone={action.tone}>{action.label}</Chip>
        {row.live_ref !== null && (
          <span className="text-[11px] text-muted-foreground">
            already #{row.live_ref}, {row.live_state?.toLowerCase()}
          </span>
        )}
        {row.changed_fields.length > 0 && row.action !== 'unchanged' && (
          <span className="text-[11px] text-muted-foreground">changes: {row.changed_fields.join(', ').replace(/_/g, ' ')}</span>
        )}
        {row.action === 'conflict' && (
          <span className="text-[11px] text-rose-700 dark:text-rose-300">
            This post is {row.live_state?.toLowerCase()}, so the document's version will not be written over it. Change it
            on the post itself if it should change.
          </span>
        )}
        {row.action === 'archived' && (
          <span className="text-[11px] text-muted-foreground">
            This post was archived on the board, so Commit will not bring it back. Restore it on the post itself if it is
            wanted again.
          </span>
        )}
        {row.open_flags > 0 && <Chip tone="red">{row.open_flags} to check</Chip>}
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_10rem_10rem_12rem]">
        <div className="space-y-1">
          <Label className="text-xs">Title</Label>
          <Input defaultValue={row.title ?? ''} disabled={!canEdit} className="h-9" onBlur={blur('title')} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Audience</Label>
          <NativeSelect
            value={row.audience ?? ''}
            disabled={!canEdit}
            className="h-9"
            onChange={(e) => void onSave({ audience: e.target.value })}
          >
            <option value="">Not set</option>
            {audiences.map((a) => (
              <option key={a.code} value={a.code}>
                {a.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Goes out on</Label>
          <Input type="date" defaultValue={row.scheduled_for ?? ''} disabled={!canEdit} className="h-9" onBlur={blur('scheduled_for')} />
          {!row.scheduled_for && row.live_scheduled_for && (
            <p className="text-[11px] text-muted-foreground">Keeps its date on the board, {shortDate(row.live_scheduled_for)}</p>
          )}
          {!row.scheduled_for && !row.live_scheduled_for && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300">No date set</p>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Call to action</Label>
          <Input defaultValue={row.cta ?? ''} disabled={!canEdit} className={cn('h-9', red('cta'))} onBlur={blur('cta')} />
          <FlagNote flag={openFlag(row, 'cta')} canAct={canEdit} onAccept={() => void onAccept('cta')} />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs">On the artwork</Label>
          <Textarea
            defaultValue={row.design_copy ?? ''}
            rows={5}
            disabled={!canEdit}
            className={cn('font-mono text-xs', red('design_copy'))}
            onBlur={blur('design_copy')}
          />
          <FlagNote flag={openFlag(row, 'design_copy')} canAct={canEdit} onAccept={() => void onAccept('design_copy')} />
        </div>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">Creative direction</Label>
            <Textarea defaultValue={row.creative_direction ?? ''} rows={2} disabled={!canEdit} className="text-xs" onBlur={blur('creative_direction')} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Figures come from</Label>
              <Input defaultValue={row.source_basis ?? ''} disabled={!canEdit} className="h-8 text-xs" onBlur={blur('source_basis')} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hashtags</Label>
              <Input
                defaultValue={(row.hashtags ?? []).join(' ')}
                disabled={!canEdit}
                className="h-8 text-xs"
                onBlur={(e) => {
                  const tags = e.target.value.split(/[\s,]+/).filter(Boolean)
                  if (tags.join(' ') === (row.hashtags ?? []).join(' ')) return
                  void onSave({ hashtags: tags })
                }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Production note</Label>
            <Input defaultValue={row.production_note ?? ''} disabled={!canEdit} className="h-8 text-xs" onBlur={blur('production_note')} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-foreground">Caption options ({captions.length})</p>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto h-7 gap-1 px-2 text-xs"
              onClick={() => setCaptions([...captions, { label: `Option ${captions.length + 1}`, body: '', hashtags: [] }])}
            >
              <Plus className="h-3.5 w-3.5" /> Add an option
            </Button>
          )}
        </div>
        {captions.length === 0 && <p className="text-[11px] text-muted-foreground">No caption options were found for this post.</p>}
        <ul className="grid gap-2 lg:grid-cols-2">
          {captions.map((c, i) => {
            const flag = openFlag(row, `caption:${i}`)
            return (
              <li key={i} className={cn('space-y-1.5 rounded-md border border-border p-2', flag && 'border-rose-400')}>
                <div className="flex items-center gap-2">
                  <Input
                    defaultValue={c.label}
                    disabled={!canEdit}
                    className="h-7 w-36 text-xs"
                    onBlur={(e) => {
                      if (e.target.value === c.label) return
                      saveCaptions(captions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                    }}
                  />
                  <span className="text-[11px] text-muted-foreground">{c.body.length} characters</span>
                  {canEdit && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${c.label}`}
                      className="ml-auto h-7 w-7 p-0 text-muted-foreground"
                      onClick={() => saveCaptions(captions.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <Textarea
                  defaultValue={c.body}
                  rows={5}
                  disabled={!canEdit}
                  className={cn('text-xs', flag && 'bg-rose-50/60 dark:bg-rose-950/30')}
                  onBlur={(e) => {
                    if (e.target.value === c.body) return
                    saveCaptions(captions.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))
                  }}
                />
                {c.hashtags.length > 0 && <p className="text-[11px] text-muted-foreground">{c.hashtags.join(' ')}</p>}
                <FlagNote flag={flag} canAct={canEdit} onAccept={() => void onAccept(`caption:${i}`)} />
              </li>
            )
          })}
        </ul>
      </div>
    </li>
  )
}

/**
 * The review of one plan upload, and its commit.
 *
 * Draws content_plan_import_review_v1 and decides nothing: which posts are new
 * or in conflict, the mix, and what blocks Commit all come from the database.
 * Every field saves on blur and the answer is re-read, so a flag cleared by an
 * edit clears because the database proved it, not because the screen hoped.
 */
export function PlanReview({
  importId,
  audiences,
  canEdit,
  onBack,
  onCommitted,
}: {
  importId: string
  audiences: CodeLabel[]
  canEdit: boolean
  onBack: () => void
  onCommitted: () => void
}) {
  const review = useGetContentPlanImport()
  const saveRow = useSaveContentPlanRow()
  const accept = useAcceptContentPlanFlag()
  const setImport = useSetContentPlanImport()
  const schedule = useScheduleContentPlanImport()
  const commit = useCommitContentPlanImport()
  const discard = useDiscardContentPlanImport()
  const fileUrl = useGetContentPlanFileUrl()
  const [busy, setBusy] = useState<string | null>(null)
  const [start, setStart] = useState('')
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])

  const trigger = review.trigger
  const load = useCallback(() => void trigger({ id: importId }, { skipCache: true }), [trigger, importId])
  useEffect(load, [load])

  const data = review.data as Review | null | undefined
  const status = data?.import.status

  // Reading takes a minute or two. Polled every four seconds while it runs, and
  // not at all once it has landed.
  useEffect(() => {
    if (status !== 'Reading') return
    const t = setInterval(load, 4000)
    return () => clearInterval(t)
  }, [status, load])

  async function run(name: string, fn: () => Promise<unknown>, ok?: string) {
    setBusy(name)
    try {
      await fn()
      if (ok) toast.success(ok)
      load()
    } catch (err) {
      toast.error('That did not go through', { description: writeErrorMessage(err) })
      load()
    } finally {
      setBusy(null)
    }
  }

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading the upload
      </div>
    )
  }

  const imp = data.import
  const editable = canEdit && imp.status === 'Pending Review'
  const labels = Object.fromEntries(audiences.map((a) => [a.code, a.label]))
  const mix = planMixLines(data.mix, labels)
  const report = imp.commit_report

  return (
    <div className="space-y-0 pb-10">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" /> Plans
        </Button>
        <p className="text-sm font-semibold text-foreground">
          {planHeading(imp.plan?.title ?? null, imp.period_month ? monthLabel(imp.period_month) : null)}
        </p>
        <Chip tone={imp.status === 'Committed' ? 'green' : imp.status === 'Failed' ? 'red' : 'amber'}>{imp.status}</Chip>
        <span className="text-[11px] text-muted-foreground">
          uploaded {shortDate(imp.created_at)} by {imp.created_by}
          {imp.cost_usd !== null ? `, read for $${Number(imp.cost_usd).toFixed(2)}` : ''}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2">
        {imp.files.map((f, i) => (
          <Button
            key={i}
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
            disabled={!f.storage_path}
            onClick={() =>
              void run('file', async () => {
                const r = (await fileUrl.trigger({ id: imp.id, index: i }).result) as { url: string }
                window.open(r.url, '_blank', 'noopener')
              })
            }
          >
            <FileText className="h-3.5 w-3.5" /> {f.filename}
          </Button>
        ))}
      </div>

      {imp.status === 'Reading' && (
        <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Reading the documents. This page updates by itself when it is done.
        </div>
      )}
      {imp.status === 'Failed' && (
        <div className="m-4 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {imp.error ?? 'The read failed.'}
        </div>
      )}

      {report && (
        <div className="m-4 space-y-1 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100">
          <p className="font-semibold">Committed to {report.plan_title}.</p>
          <p>{commitReportLine(report)}</p>
          <p>
            {countOf(report.tasks_created.length, 'task')} created
            {report.tasks_created.length > 0 ? `: ${report.tasks_created.map((t) => `#${t.ref}`).join(', ')}` : ''}.
          </p>
          {report.conflicts.length > 0 && (
            <p>
              Not written, for a person to decide:{' '}
              {report.conflicts.map((c) => `post ${c.post_no} (#${c.live_ref}, ${c.state.toLowerCase()})`).join(', ')}.
            </p>
          )}
        </div>
      )}

      {(imp.status === 'Pending Review' || imp.status === 'Committed') && (
        <>
          <section className="grid gap-4 border-b border-border px-4 py-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground">The mix, in posts</p>
              <ul className="space-y-0.5 text-xs">
                {mix.map((m) => (
                  <li key={m.code} className={cn(m.off && 'text-amber-800 dark:text-amber-300')}>
                    {m.label}: {m.planned} post{m.planned === 1 ? '' : 's'} ({m.pct}%)
                    {m.target !== null ? `, target ${m.target}% (${m.targetPosts})` : ', no target stated'}
                  </li>
                ))}
              </ul>
              {data.mix.stated && (
                <p className="text-[11px] text-muted-foreground">
                  The document's own summary says: "{data.mix.stated}". Loaded as the posts are written, not as the summary
                  says.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground">Month</p>
              <Input
                type="month"
                defaultValue={imp.period_month?.slice(0, 7) ?? ''}
                disabled={!editable}
                className="h-8 w-40"
                onBlur={(e) => {
                  if (e.target.value === (imp.period_month?.slice(0, 7) ?? '')) return
                  void run('month', () =>
                    setImport.trigger({ id: imp.id, patch: { period_month: e.target.value ? `${e.target.value}-01` : null } }).result,
                  )
                }}
              />
              {data.existing_plan_id && (
                <p className="text-[11px] text-muted-foreground">
                  This month already has a plan. Posts are matched to it by number; nothing is duplicated.
                </p>
              )}
            </div>
          </section>

          {imp.figures_missing.length > 0 && (
            <section className="space-y-1.5 border-b border-border px-4 py-3">
              <p className="text-xs font-semibold text-foreground">Figures in the document that do not appear in what was read</p>
              <p className="text-[11px] text-muted-foreground">
                Usually a figure in an introduction or a summary table. If it belongs in a post, add it there; if not, accept it.
              </p>
              <ul className="space-y-1">
                {imp.figures_missing.map((f) => (
                  <li key={f.figure} className="flex flex-wrap items-center gap-2 text-xs">
                    <Chip tone={f.accepted_by ? 'neutral' : 'red'}>{f.figure}</Chip>
                    <span className="text-muted-foreground">{f.context}</span>
                    {f.accepted_by ? (
                      <span className="text-[11px] text-muted-foreground">accepted by {f.accepted_by}</span>
                    ) : (
                      editable && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px]"
                          onClick={() =>
                            void run('fig', () => accept.trigger({ import_id: imp.id, row_id: null, key: f.figure }).result)
                          }
                        >
                          Not needed in a post
                        </Button>
                      )
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.undated > 0 && editable && (
            <section className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
              <div>
                <p className="text-xs font-semibold text-foreground">
                  {data.undated} post{data.undated === 1 ? ' has' : 's have'} no date
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Date them all at once, one per chosen day, in post order. Or leave them and a task is raised to schedule them.
                </p>
              </div>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-40" />
              <div className="flex gap-1">
                {WEEKDAYS.map((d) => (
                  <label key={d.n} className="flex items-center gap-1 text-[11px]">
                    <input
                      type="checkbox"
                      checked={days.includes(d.n)}
                      onChange={(e) => setDays(e.target.checked ? [...days, d.n] : days.filter((x) => x !== d.n))}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={!start || days.length === 0 || busy !== null}
                onClick={() =>
                  void run('schedule', () => schedule.trigger({ id: imp.id, start, weekdays: days }).result, 'Dated')
                }
              >
                Schedule them
              </Button>
            </section>
          )}

          {data.removed.length > 0 && (
            <section className="space-y-1.5 border-b border-border px-4 py-3">
              <p className="text-xs font-semibold text-foreground">In this month's plan but not in the document</p>
              <ul className="space-y-1">
                {data.removed.map((r) => (
                  <li key={r.post_no} className="flex flex-wrap items-center gap-2 text-xs">
                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={r.confirmed}
                        disabled={!editable}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...imp.remove_post_nos, r.post_no]
                            : imp.remove_post_nos.filter((n) => n !== r.post_no)
                          void run('remove', () => setImport.trigger({ id: imp.id, patch: { remove_post_nos: next } }).result)
                        }}
                      />
                      Stop post {r.post_no}, {r.title}
                    </label>
                    <span className="text-muted-foreground">#{r.live_ref}, {r.state.toLowerCase()}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <ul className="divide-y divide-border">
            {data.rows.map((row) => (
              <RowCard
                key={row.id}
                row={row}
                audiences={audiences}
                canEdit={editable}
                onSave={(patch) => run('row', () => saveRow.trigger({ id: row.id, patch }).result)}
                onAccept={(field) => run('accept', () => accept.trigger({ import_id: imp.id, row_id: row.id, key: field }).result)}
              />
            ))}
          </ul>

          {editable && (
            <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-border bg-card px-4 py-3">
              {data.blocked_reasons.length > 0 ? (
                <ul className="flex-1 space-y-0.5 text-[11px] text-rose-700 dark:text-rose-300">
                  {data.blocked_reasons.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : (
                <p className="flex-1 text-[11px] text-muted-foreground">
                  {data.rows.filter((r) => r.include && r.action === 'new').length} new,{' '}
                  {data.rows.filter((r) => r.include && r.action === 'changed').length} changed,{' '}
                  {conflictCount(data.rows.filter((r) => r.include && r.action === 'conflict').length)} that will not be written
                  {data.rows.some((r) => r.include && r.action === 'archived')
                    ? `, ${data.rows.filter((r) => r.include && r.action === 'archived').length} archived that will be left archived`
                    : ''}
                  .
                </p>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                disabled={busy !== null}
                onClick={() => void run('discard', () => discard.trigger({ id: imp.id }).result, 'Set aside. Nothing was committed.')}
              >
                Discard
              </Button>
              <Button
                type="button"
                size="sm"
                variant="brand"
                className="gap-1.5"
                disabled={busy !== null || data.blocked_reasons.length > 0}
                onClick={() =>
                  void run(
                    'commit',
                    async () => {
                      await commit.trigger({ id: imp.id }).result
                      onCommitted()
                    },
                    'Committed',
                  )
                }
              >
                {busy === 'commit' ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                Commit the plan
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
